import { useCallback } from 'react';
import LogoImg from '../../../screens/chatbotWeb/images/isa2.png';
import LogoImgSad from '../../../screens/chatbotWeb/images/isa_sad2.png';

import { Utils } from '../../../utils/Utils';

/**
 * HandleSelectOption Component
 *
 * This component handles chatbot option selection logic extracted from useChatbotWeb hook.
 * It manages various chat flows including loan processes, absence handling, and general inquiries.
 */
const useHandleSelectOption = ({
    // State setters
    setMessages,
    setOptions,
    setShowOptions,
    setShowInputBar,
    setNewTipoChat,
    setLastTypeChat,
    setLastAreaChat,
    setSelectedSessionId,
    setSelectedSession,
    setComunicacaoInternaData,
    fetchCommunicationBySession,

    // Current values
    selectedSessionId,
    profile,
    newTipoChat,
    lastTypeChat,
    lastAreaChat,
    calcularSeguro,
    setCalcularSeguro,

    // Options arrays
    defaultOptions,
    defaultOptionsNew,
    optionsSubInformacoesGerais,
    optionsManuaisTutoriais,
    finalizar,
    finalizarIsa,
    atendimentoOptions,
    finalizarAtendimento,
    optionsEmprestimo,
    ausenciaDefault,
    optionsComunicacaoInternaAreaAtestados,
    optionsComunicacaoInternaAreaDocumentos,
    optionsComunicacaoInternaAreaValeTransporte,
    optionsComunicacaoInternaAreaVR,
    optionsComunicacaoInternaAreaVRAuto,
    optionsComunicacaoInternaAreaValeAlimentacao,

    // API functions
    mudarStatusSessao,
    atribuirSessaoChat,
    sendMessageStatus,
    sendOptionAsMessage,
    openLoanModal,
    handleDetectarIntencao,
    openAddMessageModal,
    comunicacaoInternaData,
    openViewCommunicationModal,
    setLoadingPage,
    openDatePickerModal,
    selectedDate,

    fetchAreasForCommunication,
    fetchTiposForCommunication,
    fetchRegionalCommunication,
    handleComunicacaoInternaFileUpload,
    openRegionalModal,
    fetchPessoaPostoLocalTrabalho,
    confirmarAusencia,
    fetchDetalhadoAusencia,
    openPostoEscalaModal,
    openDataHoraAusenciaModal,
    setAusenciaData,
    ausenciaData,
}) => {
    const handleSelectOption = useCallback(
        async (optionKey, optionLabel, extraData) => {
            if (!selectedSessionId) return;

            if (!optionKey) optionKey = 'voltar_chat';

            const nomeUsuario = `${profile.pess_nome}-${profile.pess_id_pessoa}`;
            const letraInicial = profile.pess_nome?.[0] ?? 'U';

            // Handle different option cases
            if (optionKey === 'finalizar') {
                setSelectedSessionId(null);
                setSelectedSession(null);
                setMessages([]);
                return;
            }

            if (optionKey === 'informacoes') {
                setNewTipoChat(optionKey);
                setOptions(optionsSubInformacoesGerais);
                setShowOptions(true);
                setShowInputBar(false);
                return;
            }

            if (optionKey === 'contato_regional') {
                setNewTipoChat(optionKey);
                setShowOptions(false);
                setMessages((prev) => [
                    ...prev,
                    {
                        remetente: 'ISA',
                        texto: 'Por favor digite o nome da regional:',
                        avatar: LogoImg,
                        isOption: false,
                    },
                ]);
                setShowInputBar(true);
                return;
            }

            // Handle option logic (simplified for this example)
            if (optionKey === 'voltar_chat') {
                setNewTipoChat(optionKey);
                setShowOptions(false);
                setOptions(defaultOptionsNew);
                setMessages((prev) => [
                    ...prev,
                    {
                        remetente: 'ISA',
                        texto: 'Olá, seja bem-vindo(a) ao chat do Grupo Altum! Para continuar, selecione uma das opções abaixo ou digite claramente o que você precisa:',
                        avatar: LogoImg,
                        isOption: false,
                    },
                ]);

                setShowOptions(true);
                setShowInputBar(true);
                return;
            }

            if (optionKey === 'chatbot_isa_talk') {
                setNewTipoChat('chatbot_isa_talk');
                setOptions([]);
                setShowOptions(false);
                setShowInputBar(true);
                //setOptions(atendimentoOptions);
                return;
            }

            if (optionKey === 'voltar_questoes') {
                setNewTipoChat(optionKey);
                setOptions(defaultOptions);
                setShowOptions(true);
                setShowInputBar(false);
                return;
            }

            if (optionKey === 'manuais_turoriais') {
                setNewTipoChat(optionKey);
                setOptions(optionsManuaisTutoriais);
                setShowOptions(true);
                setShowInputBar(false);
                return;
            }

            if (optionKey === 'holerite') {
                setNewTipoChat('gerar_holerite');
                setLoadingPage(true);
                await sendOptionAsMessage('gerar_holerite', 'informacoes');
                setLoadingPage(false);
                setOptions([{ key: 'voltar_questoes', label: 'Voltar para as questões anteriores' }]);
                setShowOptions(true);
                setShowInputBar(true);
                return;
            }

            if (optionKey === 'informe') {
                setNewTipoChat('informe_de_rendimento');
                setLoadingPage(true);
                await sendOptionAsMessage('informe_de_rendimento', 'informacoes');
                setLoadingPage(false);
                setOptions([{ key: 'voltar_questoes', label: 'Voltar para as questões anteriores' }]);
                setShowOptions(true);
                setShowInputBar(true);
                return;
            }

            if (
                optionKey !== 'encerrar_chat' &&
                optionKey !== 'voltar_questoes' &&
                optionsSubInformacoesGerais.some((option) => option.key === optionKey)
            ) {
                setMessages((prev) => [...prev, { remetente: nomeUsuario, texto: optionLabel, avatar: letraInicial }]);
                setLoadingPage(true);
                await sendOptionAsMessage(optionLabel);
                setLoadingPage(false);
                setShowOptions(true);
                return;
            }

            /*ISA Atendimento*/
            if (await handleIsaAtendimento(optionKey, optionLabel)) return;

            /*Emprestimo*/
            if (await handleEmprestimo(optionKey, optionLabel)) return;

            /*Ausência*/
            if (await handleAusencia(optionKey, optionLabel, extraData)) return;

            /*Comunicação interna atendimento*/
            if (await handleComunicacaoInternaAtendimentoModalInterativo(optionKey, optionLabel)) return;

            /*Comunicação interna chat modal*/
            if (await handleComunicacaoInternaModal(optionKey, optionLabel)) return;

            /*Comunicação interna chat interação*/
            if (await handleSelectOptionComunicaoInternaInteracao(optionKey, optionLabel)) return;

            // Default handler for unhandled options
            setMessages((prev) => [...prev, { remetente: nomeUsuario, texto: optionLabel, avatar: letraInicial }]);
            setLoadingPage(true);
            await sendOptionAsMessage(optionLabel);
            setLoadingPage(false);
            setShowOptions(false);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            selectedSessionId,
            profile,
            defaultOptions,
            defaultOptionsNew,
            optionsSubInformacoesGerais,
            optionsManuaisTutoriais,
            finalizar,
            finalizarIsa,
            atendimentoOptions,
            finalizarAtendimento,
            optionsEmprestimo,
            ausenciaDefault,
            mudarStatusSessao,
            atribuirSessaoChat,
            sendMessageStatus,
            sendOptionAsMessage,
            openLoanModal,
            setMessages,
            setOptions,
            setShowOptions,
            setShowInputBar,
            setNewTipoChat,
            setLastTypeChat,
            setSelectedSessionId,
            setSelectedSession,
            fetchCommunicationBySession,
        ],
    );

    const handleIsaAtendimento = async (optionKey, optionLabel) => {
        if (optionKey === 'problema_resolvido') {
            mudarStatusSessao('RESOLVIDO');
            setMessages((prev) => [
                ...prev,
                {
                    remetente: 'ISA',
                    texto: 'Obrigado, volte sempre que precisar de ajuda!',
                    avatar: LogoImg,
                    isOption: false,
                },
            ]);

            setOptions([{ key: 'finalizar', label: 'Encerrar o Chat' }]);
            setShowOptions(true);
            return true;
        }

        if (optionKey === 'problema_nao_resolvido') {
            mudarStatusSessao('NAO_RESOLVIDO');
            setShowOptions(false);
            setNewTipoChat('problema_nao_resolvido_finalizar');
            setMessages((prev) => [
                ...prev,
                {
                    remetente: 'ISA',
                    texto: 'Por favor, conte-nos com detalhes o que aconteceu?',
                    avatar: LogoImgSad,
                    isOption: false,
                },
            ]);
            setShowInputBar(true);
            return true;
        }

        if (optionKey === 'problema_nao_resolvido_finalizar') {
            setOptions([{ key: 'finalizar', label: 'Encerrar o Chat' }]);
            setMessages((prev) => [
                ...prev,
                {
                    remetente: 'ISA',
                    texto: 'Agradecemos pela sua opinião!',
                    avatar: LogoImg,
                    isOption: false,
                },
            ]);

            setShowOptions(true);
            return true;
        }

        if (optionKey === 'atribuir') {
            setOptions([{ key: 'finalizar_atendimento', label: 'Encerrar o Chat' }]);
            await mudarStatusSessao('PENDENTE_HUMANO');
            await atribuirSessaoChat('PENDENTE');
            sendMessageStatus({
                remetente: 'ISA',
                texto: 'Atendente assumiu o atendimento e está pronto para ajudar',
                avatar: LogoImg,
                isOption: false,
            });

            setOptions([{ key: 'finalizar_atendimento', label: 'Encerrar o atendimento' }]);
            setShowOptions(true);
            setShowInputBar(true);
            return true;
        }

        if (optionKey === 'encerrar_chat') {
            setNewTipoChat(optionKey);
            setOptions(optionsSubInformacoesGerais);
            setShowOptions(true);
            setMessages((prev) => [
                ...prev,
                {
                    remetente: 'ISA',
                    texto: 'Você conseguiu resolver o problema?',
                    avatar: LogoImg,
                    isOption: false,
                },
            ]);
            setOptions(finalizar);
            setShowInputBar(false);
            return true;
        }

        if (optionKey === 'encerrar_chat_isa') {
            setNewTipoChat('voltar_chat');
            setShowOptions(true);
            setOptions(finalizarIsa);
            setShowInputBar(true);
            return true;
        }

        if (optionKey === 'call_human') {
            setNewTipoChat('voltar_questoes');
            mudarStatusSessao('AGUARDANDO_HUMANDO');
            setNewTipoChat('AGUARDANDO_HUMANDO');

            setOptions([{ key: 'finalizar', label: 'Encerrar o Chat' }]);
            setMessages((prev) => [
                ...prev,
                {
                    remetente: 'ISA',
                    texto: 'Em breve um de nossos atendentes entrará em contato. Obrigado',
                    avatar: LogoImg,
                    isOption: false,
                },
            ]);
            setShowOptions(true);
            setShowInputBar(true);
            return true;
        }

        if (optionKey === 'voltar_pendente_atendimento') {
            await atribuirSessaoChat('NAO_RESOLVIDO');
            await mudarStatusSessao('AGUARDANDO_HUMANDO');
            setMessages((prev) => [
                ...prev,
                {
                    remetente: 'ISA',
                    texto: 'O atendimento voltou para pendente',
                    avatar: LogoImg,
                    isOption: false,
                },
            ]);

            sendMessageStatus({
                remetente: 'ISA',
                texto: 'Atendente voltou o atendimento para pendente, aguardando outro atendente para resolver',
                avatar: LogoImg,
                isOption: false,
            });

            setOptions([{ key: 'finalizar', label: 'Encerrar o Chat' }]);
            setShowOptions(true);
            return true;
        }

        if (optionKey === 'cancelar_atendimento') {
            await mudarStatusSessao('CANCELADO');
            await atribuirSessaoChat('CANCELADO');
            setMessages((prev) => [
                ...prev,
                {
                    remetente: 'ISA',
                    texto: 'Atendimento Cancelado!',
                    avatar: LogoImg,
                    isOption: false,
                },
            ]);

            sendMessageStatus({
                remetente: 'ISA',
                texto: 'Atendente cancelou o atendimento',
                avatar: LogoImg,
                isOption: false,
            });

            setOptions([{ key: 'finalizar', label: 'Encerrar o Chat' }]);
            setShowOptions(true);
            return true;
        }

        if (optionKey === 'finalizar_atendimento') {
            setNewTipoChat(optionKey);
            setOptions(optionsSubInformacoesGerais);
            setShowOptions(true);
            setMessages((prev) => [
                ...prev,
                {
                    remetente: 'ISA',
                    texto: 'Você conseguiu resolver o problema?',
                    avatar: LogoImg,
                    isOption: false,
                },
            ]);
            setOptions(finalizarAtendimento);
            setShowInputBar(false);
            return true;
        }

        if (optionKey === 'problema_resolvido_atendimento') {
            await mudarStatusSessao('RESOLVIDO_HUMANO');
            await atribuirSessaoChat('RESOLVIDO');
            setMessages((prev) => [
                ...prev,
                {
                    remetente: 'ISA',
                    texto: 'Parabêns, obrigado por ajudar!',
                    avatar: LogoImg,
                    isOption: false,
                },
            ]);

            sendMessageStatus({
                remetente: 'ISA',
                texto: 'Atendente resolveu o problema, obrigado!',
                avatar: LogoImg,
                isOption: false,
            });

            setOptions([{ key: 'finalizar', label: 'Encerrar o Chat' }]);
            setShowOptions(true);
            return true;
        }

        if (optionKey === 'problema_nao_resolvido_atendimento') {
            await mudarStatusSessao('NAO_RESOLVIDO_HUMANO');
            await atribuirSessaoChat('NAO_RESOLVIDO');
            setMessages((prev) => [
                ...prev,
                {
                    remetente: 'ISA',
                    texto: 'Que pena que não deu certo..',
                    avatar: LogoImg,
                    isOption: false,
                },
            ]);

            sendMessageStatus({
                remetente: 'ISA',
                texto: 'Problema não resolvido pelo atendente. Suas informações serão analizadas pelos nossos especialistas',
                avatar: LogoImg,
                isOption: false,
            });

            setOptions([{ key: 'finalizar', label: 'Encerrar o Chat' }]);
            setShowOptions(true);
            return true;
        }
        return false;
    };

    function validateBrazilianMobile(input) {
        if (!input) return false;

        // 1. Remove non-digits
        const digits = String(input).replace(/\D/g, '');

        if (digits.length === 11) {
            return digits[2] === '9';
        }

        if (digits.length === 13) {
            return digits[4] === '9';
        }

        return false;
    }

    const handleEmprestimo = async (optionKey, optionLabel) => {
        if (optionKey === 'emprestimo') {
            setNewTipoChat(optionKey);
            setOptions(optionsEmprestimo);
            setShowOptions(true);
            setShowInputBar(false);
            //sendOptionWithMessage(optionLabel);
            return true;
        }

        if (optionKey === 'emprestimo_inicio_escolher') {
            setNewTipoChat('voltar_chat');
            setOptions([
                {
                    key: 'emprestimo_inicio',
                    label: 'Iniciar processo de empréstimo consignado',
                },
            ]);
            setShowOptions(true);
            setShowInputBar(true);
            return true;
        }

        if (optionKey === 'emprestimo_inicio') {
            setNewTipoChat('credito_consignado');

            setOptions([{ key: 'voltar_chat', label: 'Sair do Módulo de Empréstimo' }]);
            setShowOptions(true);
            setLoadingPage(true);
            setLoadingPage(false);

            if (validateBrazilianMobile(profile.pess_telefone)) {
                setMessages((prev) => [
                    ...prev,
                    {
                        remetente: 'ISA',
                        texto: 'Estamos iniando o procesos de emprestimo, por favor insira sua data de nascimento, por exemplo: DD/MM/AAAA, caso não queira solitar emprestimo agora, clique em Sair do Módulo de Empréstimo',
                        avatar: LogoImg,
                        isOption: false,
                    },
                ]);

                setShowInputBar(true);
                return true;
            }

            setMessages((prev) => [
                ...prev,
                {
                    remetente: 'ISA',
                    texto: 'Por favor insira o numero do seu celular com ddd e sua data de nascimento, por exemplo: (xx)xxxxx-xxxx - DD/MM/AAAA',
                    avatar: LogoImg,
                    isOption: false,
                },
            ]);

            setShowInputBar(true);
            return true;
        }

        if (optionKey === 'emprestimo_tentar_novamente') {
            setNewTipoChat('credito_consignado');

            setOptions([
                {
                    key: 'emprestimo_inicio',
                    label: 'Tentar novamente após assinar a autorização de consulta dos seus dados',
                },
                { key: 'voltar_chat', label: 'Sair do Módulo de Empréstimo' },
            ]);
            setShowOptions(true);
            setLoadingPage(true);
            setLoadingPage(false);

            setShowInputBar(false);
            return true;
        }

        if (optionKey === 'credito_consignado') {
            setCalcularSeguro('');
            setNewTipoChat('credito_consignado');
            setOptions([
                { key: 'emprestimo_continuar', label: 'Calcular Simulação por Parcela' },
                { key: 'emprestimo_continuar_valor_total', label: 'Calcular Simulação pelo Valor Total' },
                { key: 'credito_cancelar', label: 'Cancelar proposta existente' },
            ]);
            setShowOptions(true);
            setShowInputBar(false);
            return true;
        }

        if (optionKey === 'emprestimo_continuar') {
            if (calcularSeguro === '') {
                setShowOptions(false);
                setShowInputBar(false);
                setNewTipoChat('credito_inserir_seguro_calculo');
                setLastTypeChat('credito_calcular_simulacao');

                setMessages((prev) => [
                    ...prev,
                    {
                        remetente: 'ISA',
                        texto: 'Gostaria de incluir o valor do seguro?',
                        avatar: LogoImg,
                        isOption: false,
                    },
                ]);

                setOptions([
                    { key: 'credito_inserir_seguro_calculo_sim', label: 'SIM' },
                    { key: 'credito_inserir_seguro_calculo_nao', label: 'NÃO' },
                    { key: 'credito_cancelar', label: 'Cancelar proposta existente' },
                ]);
                setShowOptions(true);
                setShowInputBar(false);
                return true;
            }
            setShowOptions(false);
            setShowInputBar(false);
            setNewTipoChat('credito_calcular_simulacao');
            setLastTypeChat('credito_calcular_simulacao');
            setOptions([{ key: 'credito_cancelar', label: 'Cancelar proposta existente' }]);
            setMessages((prev) => [
                ...prev,
                {
                    remetente: 'ISA',
                    texto: 'Por favor insira o valor da parcela igual ou menor ao valor da mensagem anterior.',
                    avatar: LogoImg,
                    isOption: false,
                },
            ]);

            setShowOptions(true);
            setShowInputBar(true);
            return true;
        }

        if (optionKey === 'credito_calcular_simulacao') {
            setShowOptions(false);
            setShowInputBar(false);
            setNewTipoChat('credito_calcular_simulacao');
            setLastTypeChat('credito_calcular_simulacao');
            setOptions([
                { key: 'credito_salvar_simulacao', label: 'Continuar com as condições' },
                { key: 'credito_consignado', label: 'Mudar o tipo de cálculo' },
                { key: 'credito_cancelar', label: 'Cancelar proposta existente' },
            ]);
            setMessages((prev) => [
                ...prev,
                {
                    remetente: 'ISA',
                    texto: 'Por favor insira um novo valor para calcular o valor da parcela e use a palavra "SIM" caso queira incluir o "Seguro" ou clique em "Continuar com as condições atuais"',
                    avatar: LogoImg,
                    isOption: false,
                },
            ]);

            setShowOptions(true);
            setShowInputBar(true);
            return true;
        }

        if (optionKey === 'emprestimo_continuar_valor_total') {
            if (calcularSeguro === '') {
                setShowOptions(false);
                setShowInputBar(false);
                setNewTipoChat('credito_inserir_seguro_calculo');
                setLastTypeChat('credito_valor_total_calcular_simulacao');

                setMessages((prev) => [
                    ...prev,
                    {
                        remetente: 'ISA',
                        texto: 'Gostaria de incluir o valor do seguro?',
                        avatar: LogoImg,
                        isOption: false,
                    },
                ]);

                setOptions([
                    { key: 'credito_inserir_seguro_calculo_sim', label: 'SIM' },
                    { key: 'credito_inserir_seguro_calculo_nao', label: 'NÃO' },
                    { key: 'credito_cancelar', label: 'Cancelar proposta existente' },
                ]);
                setShowOptions(true);
                setShowInputBar(false);
                return true;
            }

            setShowOptions(false);
            setShowInputBar(false);
            setNewTipoChat('credito_valor_total_calcular_simulacao');
            setLastTypeChat('credito_valor_total_calcular_simulacao');
            setOptions([{ key: 'credito_cancelar', label: 'Cancelar proposta existente' }]);
            setMessages((prev) => [
                ...prev,
                {
                    remetente: 'ISA',
                    texto: 'Por favor insira um valor para calcular o valor total do empréstimo.',
                    avatar: LogoImg,
                    isOption: false,
                },
            ]);

            setShowOptions(true);
            setShowInputBar(true);
            return true;
        }

        if (optionKey === 'credito_inserir_seguro_calculo_sim' || optionKey === 'credito_inserir_seguro_calculo_nao') {
            const seguroVal = optionKey === 'credito_inserir_seguro_calculo_sim' ? ' SIM' : ' NAO';
            setCalcularSeguro(seguroVal);

            setShowOptions(false);
            setShowInputBar(false);
            setNewTipoChat(lastTypeChat);
            setLastTypeChat(lastTypeChat);
            setOptions([{ key: 'credito_cancelar', label: 'Cancelar proposta existente' }]);

            setMessages((prev) => [
                ...prev,
                {
                    remetente: 'ISA',
                    texto:
                        lastTypeChat === 'credito_valor_total_calcular_simulacao'
                            ? 'Por favor insira um valor para calcular o valor total do empréstimo.'
                            : 'Por favor insira o valor da parcela igual ou menor ao valor da mensagem anterior.',
                    avatar: LogoImg,
                    isOption: false,
                },
            ]);

            setShowOptions(true);
            setShowInputBar(true);
            return true;
        }

        if (optionKey === 'credito_valor_total_calcular_simulacao') {
            setShowOptions(false);
            setShowInputBar(false);
            setNewTipoChat('credito_valor_total_calcular_simulacao');
            setLastTypeChat('credito_valor_total_calcular_simulacao');

            setMessages((prev) => [
                ...prev,
                {
                    remetente: 'ISA',
                    texto: 'Por favor insira um novo valor para calcular o valor total do empréstimo e use a palavra "SIM" caso queira incluir o "Seguro" ou clique em "Continuar com as condições atuais"',
                    avatar: LogoImg,
                    isOption: false,
                },
            ]);
            setOptions([
                { key: 'credito_salvar_simulacao', label: 'Continuar com as condições' },
                { key: 'credito_consignado', label: 'Mudar o tipo de cálculo' },
                { key: 'credito_cancelar', label: 'Cancelar proposta existente' },
            ]);

            setShowOptions(true);
            setShowInputBar(true);
            return true;
        }

        if (optionKey === 'credito_salvar_simulacao') {
            setShowOptions(false);
            setShowInputBar(false);
            setLoadingPage(true);

            const result = await sendOptionAsMessage(optionKey, 'credito_consignado');

            setLoadingPage(false);

            if (!result) {
                setMessages((prev) => [
                    ...prev,
                    {
                        remetente: 'ISA',
                        texto: 'Ops! Não conseguimos salvar sua simulação agora. Não se preocupe, tente novamente em instantes.',
                        avatar: LogoImgSad,
                        isOption: false,
                    },
                ]);
                await handleSelectOption(lastTypeChat);
                return true;
            }

            setNewTipoChat('credito_salvar_dados_complementares');
            setOptions([
                { key: 'emprestimo_abrir_janela_atualizacao', label: 'Atualizar dados cadastrais' },
                { key: 'credito_cancelar', label: 'Cancelar proposta' },
            ]);
            setShowOptions(true);
            setShowInputBar(false);
            return true;
        }

        if (optionKey === 'emprestimo_abrir_janela_atualizacao') {
            // Open the loan data modal
            await openLoanModal();
            return true;
        }

        if (optionKey === 'credito_cancelar_pre') {
            setNewTipoChat('credito_consignado');

            setOptions([
                { key: 'voltar_questoes', label: 'Voltar para as questões anteriores' },
                { key: 'credito_cancelar', label: 'Cancelar proposta' },
            ]);

            setMessages((prev) => [
                ...prev,
                {
                    remetente: 'ISA',
                    texto: 'Tem certeza que deseja cancelar a sua proposta?',
                    avatar: LogoImg,
                    isOption: false,
                },
            ]);

            setShowOptions(true);
            setShowInputBar(false);
            return true;
        }

        if (optionKey === 'emprestimo_finalizar' || optionKey === 'credito_consignado_aprovada_aderir_proposta') {
            setNewTipoChat('credito_finalizar');

            setMessages((prev) => [
                ...prev,
                {
                    remetente: 'ISA',
                    texto: 'Gostaria de aderir ao empréstimo? Se sim, clique em aderir',
                    avatar: LogoImg,
                    isOption: false,
                },
            ]);

            setOptions([
                { key: 'credito_finalizar', label: 'Aderir' },
                { key: 'credito_cancelar', label: 'Cancelar proposta' },
            ]);

            setShowOptions(true);
            setShowInputBar(false);
            return true;
        }

        if (optionKey === 'credito_cancelar') {
            setNewTipoChat('credito_cancelar');

            setOptions([{ key: 'finalizar', label: 'Encerrar o Chat' }]);
            setLoadingPage(true);
            await sendOptionAsMessage(optionKey, 'credito_consignado');
            setLoadingPage(false);
            await mudarStatusSessao('RESOLVIDO');
            setShowOptions(true);
            setShowInputBar(false);
            return true;
        }

        if (optionKey === 'credito_finalizar') {
            setNewTipoChat('credito_finalizar');

            setOptions([{ key: 'finalizar', label: 'Encerrar o Chat' }]);

            setLoadingPage(true);
            const result = await sendOptionAsMessage(optionKey, 'credito_consignado');
            setLoadingPage(false);
            if (!result) {
                setOptions([
                    { key: 'credito_finalizar', label: 'Aderir' },
                    { key: 'finalizar', label: 'Encerrar o Chat' },
                ]);
                return true;
            } else {
                await mudarStatusSessao('EMPRESTIMO_FINALIZADO');
                setMessages((prev) => [
                    ...prev,
                    {
                        remetente: 'ISA',
                        texto: 'Tudo pronto por aqui! Para concluir a contratação, basta acessar o link abaixo e assinar sua proposta.',
                        avatar: LogoImg,
                        isOption: false,
                    },
                ]);
                setShowOptions(true);
                setShowInputBar(true);
                return true;
            }
        }

        //opção opcional credito_consignado_efetivada não implementar ainda

        if (optionKey === 'credito_consignado_averbada') {
            setShowInputBar(false);

            setLoadingPage(true);
            setNewTipoChat('voltar_chat');
            setOptions([
                { key: 'voltar_chat', label: 'Sair do Módulo de Empréstimo' },
                { key: 'finalizar', label: 'Encerrar o Chat' },
            ]);
            setLoadingPage(false);
            setShowOptions(true);
            setShowInputBar(false);
            return true;
        }

        if (optionKey === 'conversar_emprestimo') {
            setOptions([{ key: 'finalizar_atendimento', label: 'Encerrar o Chat' }]);
            setNewTipoChat(optionKey);
            setShowOptions(true);
            setShowInputBar(true);
            return true;
        }

        if (optionKey === 'atribuir_emprestimo') {
            setOptions([{ key: 'conversar_emprestimo', label: 'Conversar com Empréstimo' }]);
            await mudarStatusSessao('EMPRESTIMO_EM_ATEND');
            await atribuirSessaoChat('EMPRESTIMO_EM_ATEND');

            setOptions([{ key: 'finalizar_atendimento_emprestimo', label: 'Encerrar o atendimento empréstimo' }]);
            setShowOptions(true);
            setShowInputBar(true);
            return true;
        }

        if (optionKey === 'finalizar_atendimento_emprestimo') {
            setNewTipoChat(optionKey);
            setOptions([
                { key: 'finalizar_atendimento_emprestimo_sim', label: 'Sim' },
                { key: 'finalizar_atendimento_emprestimo_nao', label: 'Não' },
            ]);
            setShowOptions(true);
            setMessages((prev) => [
                ...prev,
                {
                    remetente: 'ISA',
                    texto: 'Você conseguiu resolver o problema?',
                    avatar: LogoImg,
                    isOption: false,
                },
            ]);
            setShowInputBar(false);
            return true;
        }

        if (optionKey === 'finalizar_atendimento_emprestimo_nao') {
            await mudarStatusSessao('EMPRESTIMO_FINALIZADO');
            await atribuirSessaoChat('EMPRESTIMO_FINALIZADO');
            setMessages((prev) => [
                ...prev,
                {
                    remetente: 'ISA',
                    texto: 'Atendimento finalizado',
                    avatar: LogoImg,
                    isOption: false,
                },
            ]);

            setOptions([{ key: 'finalizar', label: 'Encerrar o Chat' }]);
            setShowInputBar(false);
            setShowOptions(true);
            return true;
        }

        if (optionKey === 'finalizar_atendimento_emprestimo_sim') {
            await mudarStatusSessao('EMPRESTIMO_FIN_ATEND');
            await atribuirSessaoChat('EMPRESTIMO_FIN_ATEND');
            setMessages((prev) => [
                ...prev,
                {
                    remetente: 'ISA',
                    texto: 'Obrigado, volte sempre que precisar de ajuda!',
                    avatar: LogoImg,
                    isOption: false,
                },
            ]);

            setOptions([{ key: 'finalizar', label: 'Encerrar o Chat' }]);
            setShowInputBar(false);
            setShowOptions(true);
            return true;
        }

        return false;
    };

    const handleAusencia = async (optionKey, optionLabel, extraData) => {
        //ausencia
        const activeAusenciaData = { ...ausenciaData, ...extraData };

        if (optionKey === 'envio_de_atestados_escolher') {
            setNewTipoChat('voltar_chat');

            setOptions([{ key: 'envio_de_atestados', label: 'Iniciar o processo para informar ausência' }]);

            setShowOptions(true);
            setShowInputBar(true);
            return true;
        }

        if (optionKey === 'envio_de_atestados' || optionKey === 'ausencia_contrato_mensal_sim') {
            setNewTipoChat('ausencia_contrato_mensal_sim');

            setOptions(ausenciaDefault);

            setLoadingPage(true);
            let isaPergunta = 'Lembre-se de informar sua ausência ao seu supervisor.';
            setMessages((prev) => [
                ...prev,
                {
                    remetente: 'ISA',
                    texto: isaPergunta,
                    avatar: LogoImg,
                    isOption: false,
                },
            ]);
            setLoadingPage(false);

            setLoadingPage(true);
            await sendOptionAsMessage(isaPergunta, 'isa_pergunta');
            setLoadingPage(false);

            let userPostoData = null;
            try {
                setLoadingPage(true);
                const data = await fetchPessoaPostoLocalTrabalho();
                if (data && data !== 'posto_trabalho_nao_localizado') {
                    userPostoData = data;
                }
            } catch (error) {
                console.error('Erro ao buscar dados do posto/escala:', error);
            } finally {
                setLoadingPage(false);
            }

            if (userPostoData) {
                // Show message to user as ISA
                const formatadoMsg = `Esse é seu posto de trabalho? ${userPostoData.formatado}`;
                setMessages((prev) => [
                    ...prev,
                    {
                        remetente: 'ISA',
                        texto: formatadoMsg,
                        avatar: LogoImg,
                        isOption: false,
                    },
                ]);

                // Save data in session / state
                const detailed = userPostoData.detalhado;
                const pessoa = userPostoData.pessoa;
                const newAusenciaData = {
                    ...activeAusenciaData,
                    nome_posto: detailed?.descricao || activeAusenciaData?.nome_posto || '',
                    escala_posto: pessoa?.nome_escala || activeAusenciaData?.escala_posto || '',
                    dh_ausencia: activeAusenciaData?.dh_ausencia || '0000-00-00T00:00:00',
                    dh_ausencia_final: activeAusenciaData?.dh_ausencia_final || '0000-00-00T00:00:00',
                    nome_funcionario:
                        profile?.pess_nome ||
                        activeAusenciaData?.nome_funcionario ||
                        activeAusenciaData?.pess_nome ||
                        '',
                    codigo: detailed?.codigo ? detailed.codigo.toString() : activeAusenciaData?.codigo || '',
                    pess_nome: profile?.pess_nome || activeAusenciaData?.pess_nome || '',
                    re: pessoa?.matricula?.toString() || profile?.re || activeAusenciaData?.re || '',
                    usuario_criacao: profile?.pess_id_pessoa,
                    cliente_id: profile?.pess_id_cliente_principal ?? null,
                    status_solicitacao: Utils.calculateStatusSolicitacao(
                        activeAusenciaData?.dh_ausencia || '0000-00-00T00:00:00',
                    ),
                    codigo_posto: detailed?.codigo.toString() || activeAusenciaData?.codigo || '',
                    bairro: detailed?.bairro || activeAusenciaData?.bairro || '',
                    cep: detailed?.cep || activeAusenciaData?.cep || '',
                    cidade: detailed?.cidade || activeAusenciaData?.cidade || '',
                    endereco: detailed?.endereco || activeAusenciaData?.endereco || '',
                    endereco_complemento:
                        detailed?.endereco_complemento || activeAusenciaData?.endereco_complemento || '',
                    endereco_numero: detailed?.endereco_numero || activeAusenciaData?.endereco_numero || '',
                    nome_gestor_posto: detailed?.nome_gestor_posto || activeAusenciaData?.nome_gestor_posto || '',
                    normas_importantes_posto:
                        detailed?.normas_importantes_posto || activeAusenciaData?.normas_importantes_posto || '',
                    perfil: detailed?.perfil || activeAusenciaData?.perfil || '',
                    prioridade: Utils.calculatePrioridade(activeAusenciaData?.dh_ausencia || '0000-00-00T00:00:00'),
                    procedimentos_especificos_posto:
                        detailed?.procedimentos_especificos_posto ||
                        activeAusenciaData?.procedimentos_especificos_posto ||
                        '',
                    uf: detailed?.uf || activeAusenciaData?.uf || '',
                };

                setAusenciaData(newAusenciaData);

                // Show options to confirm or correct
                setOptions([
                    { key: 'ausencia_confirmar_escala_trabalho_data', label: 'Sim, quero continuar com esses dados' },
                    { key: 'ausencia_confirmar_escala_trabalho_cadastrar_posto', label: 'Não. quero corrigir' },
                    { key: 'voltar_chat', label: 'Desistir' },
                ]);
                setShowOptions(true);
                setShowInputBar(false);
            } else {
                // If not found, open the PostoEscalaModal to let user input manually
                let isaPergunta2 = 'Por favor preencha esses dados caso queira continuar com a ausencia.';
                setMessages((prev) => [
                    ...prev,
                    {
                        remetente: 'ISA',
                        texto: isaPergunta2,
                        avatar: LogoImg,
                        isOption: false,
                    },
                ]);

                setLoadingPage(true);
                await sendOptionAsMessage(isaPergunta2, 'isa_pergunta');
                setLoadingPage(false);

                if (openPostoEscalaModal) {
                    openPostoEscalaModal();
                }
            }
            return true;
        }

        if (optionKey === 'ausencia_posto_escala_submited') {
            const isaPergunta = `Os dados estão corretos?\nNome: ${activeAusenciaData?.pess_nome}\nMatrícula: ${activeAusenciaData?.re}\nPosto: ${activeAusenciaData?.nome_posto}\nEscala: ${activeAusenciaData?.escala_posto}`;
            setMessages((prev) => [
                ...prev,
                { remetente: 'ISA', texto: isaPergunta, avatar: LogoImg, isOption: false },
            ]);
            setOptions([
                { key: 'ausencia_confirmar_escala_trabalho_data', label: 'Sim, quero continuar com esses dados' },
                { key: 'ausencia_confirmar_escala_trabalho_cadastrar_posto', label: 'Não. quero corrigir' },
                { key: 'voltar_chat', label: 'Desistir' },
            ]);
            setShowOptions(true);
            setShowInputBar(false);
            return true;
        }

        if (optionKey === 'ausencia_confirmar_escala_trabalho_cadastrar_posto') {
            //ABRIR MODAL PARA INFORMAR: Nome, Matricula, Posto de Trabalho, Escala de Trabalho
            if (openPostoEscalaModal) {
                openPostoEscalaModal();
            }
            return true;
        }

        if (optionKey === 'ausencia_confirmar_escala_trabalho_data') {
            //informe a data e horario
            if (openDataHoraAusenciaModal) {
                openDataHoraAusenciaModal();
            }
            return true;
        }

        if (optionKey === 'ausencia_data_hora_submited') {
            const dh = activeAusenciaData?.dh_ausencia;
            const dhFinal = activeAusenciaData?.dh_ausencia_final;

            const formatDH = (datetimeStr) => {
                if (!datetimeStr) return '';
                const parts = datetimeStr.split('T');
                if (parts.length === 2) {
                    const [datePart, timePart] = parts;
                    const dateSplit = datePart.split('-');
                    if (dateSplit.length === 3) {
                        const [year, month, day] = dateSplit;
                        const timeSplit = timePart.split(':');
                        if (timeSplit.length >= 2) {
                            const [hour, minute] = timeSplit;
                            return `${day}/${month}/${year} às ${hour}:${minute}`;
                        }
                    }
                }
                return datetimeStr;
            };

            const dhFormatado = formatDH(dh);
            const dhFinalFormatado = formatDH(dhFinal);

            const isaPergunta = `Período selecionado:\nInício: ${dhFormatado}\nTérmino: ${dhFinalFormatado}\n\nGostaria de confirmar a ausência?`;
            setMessages((prev) => [
                ...prev,
                { remetente: 'ISA', texto: isaPergunta, avatar: LogoImg, isOption: false },
            ]);
            setOptions([
                { key: 'ausencia_confirmar_tudo', label: 'Sim, confirmar' },
                { key: 'ausencia_confirmar_escala_trabalho_data', label: 'Não, quero alterar a data/hora' },
            ]);
            setShowOptions(true);
            setShowInputBar(false);
            return true;
        }

        if (optionKey === 'ausencia_confirmar_tudo') {
            try {
                const payload = {
                    nome_posto: activeAusenciaData?.nome_posto || '',
                    escala_posto: activeAusenciaData?.escala_posto || '',
                    dh_ausencia: activeAusenciaData?.dh_ausencia || '0000-00-00T00:00:00',
                    dh_ausencia_final: activeAusenciaData?.dh_ausencia_final || '0000-00-00T00:00:00',
                    nome_funcionario: activeAusenciaData?.nome_funcionario || activeAusenciaData?.pess_nome || '',
                    re: activeAusenciaData?.re || '',
                    codigo_posto: activeAusenciaData?.codigo || '',
                    pess_nome: activeAusenciaData?.pess_nome || '',
                    codigo: activeAusenciaData?.codigo || '',
                    bairro: activeAusenciaData?.bairro || '',
                    cep: activeAusenciaData?.cep || '',
                    cidade: activeAusenciaData?.cidade || '',
                    endereco: activeAusenciaData?.endereco || '',
                    endereco_complemento: activeAusenciaData?.endereco_complemento || '',
                    endereco_numero: activeAusenciaData?.endereco_numero || '',
                    nome_gestor_posto: activeAusenciaData?.nome_gestor_posto || '',
                    normas_importantes_posto: activeAusenciaData?.normas_importantes_posto || '',
                    perfil: activeAusenciaData?.perfil || '',
                    prioridade: Utils.calculatePrioridade(activeAusenciaData?.dh_ausencia || '0000-00-00T00:00:00'),
                    procedimentos_especificos_posto: activeAusenciaData?.procedimentos_especificos_posto || '',
                    uf: activeAusenciaData?.uf || '',
                    status_solicitacao: Utils.calculateStatusSolicitacao(
                        activeAusenciaData?.dh_ausencia || '0000-00-00T00:00:00',
                    ),
                    cliente_id: profile?.pess_id_cliente_principal ?? null,
                    usuario_criacao: profile?.pess_id_pessoa,
                };

                setLoadingPage(true);
                await confirmarAusencia(selectedSessionId, payload);
                setLoadingPage(false);
            } catch (error) {
                console.error('Erro ao confirmar ausência:', error);
                setMessages((prev) => [
                    ...prev,
                    { remetente: 'ISA', texto: 'Erro ao confirmar ausência', avatar: LogoImg, isOption: false },
                ]);
                setLoadingPage(true);
                await handleAusencia('ausencia_confirmar_escala_trabalho_cadastrar_posto', undefined, extraData);
                setLoadingPage(false);
                return false;
            }

            setLoadingPage(true);
            await handleAusencia('ausencia_confirmar_escala_trabalho_sim', undefined, extraData);
            setLoadingPage(false);
            return true;
        }

        if (optionKey === 'ausencia_confirmar_escala_trabalho_sim') {
            setNewTipoChat('ausencia_finalizar');

            setLoadingPage(true);
            await mudarStatusSessao('AUSENCIA_EM_FINALIZACAO');
            setLoadingPage(false);

            let isaPergunta =
                'Informe o motivo da sua ausência, se puder, anexe o atestado médico ou qualquer outro documento que comprove-a. Após informar, finalize o atendimento';

            setLoadingPage(true);
            await sendOptionAsMessage(isaPergunta, 'isa_pergunta');
            setLoadingPage(false);

            setLoadingPage(true);
            setMessages((prev) => [
                ...prev,
                {
                    remetente: 'ISA',
                    texto: isaPergunta,
                    avatar: LogoImg,
                    isOption: false,
                },
            ]);
            setLoadingPage(false);

            setOptions([
                { key: 'informar_ausencia', label: 'Finalizar o atendimento' },
                { key: 'finalizar', label: 'Concluir mais tarde' },
            ]);
            setShowOptions(true);
            setShowInputBar(true);
            return true;
        }

        if (optionKey === 'ausencia_finalizar') {
            setNewTipoChat('ausencia_finalizar');
            setOptions([
                { key: 'informar_ausencia', label: 'Finalizar o atendimento' },
                { key: 'finalizar', label: 'Concluir mais tarde' },
            ]);
            setShowOptions(true);
            setShowInputBar(true);
            return true;
        }

        if (optionKey === 'informar_ausencia') {
            setNewTipoChat('ausencia_finalizar');
            setLoadingPage(true);
            let targetStatus = '';
            try {
                const data = await fetchDetalhadoAusencia(selectedSessionId);
                if (data?.cobertura_posto?.status_solicitacao) {
                    targetStatus = data.cobertura_posto.status_solicitacao;
                }
            } catch (error) {
                console.error('Erro ao buscar status_solicitacao:', error);
            }

            await mudarStatusSessao(targetStatus);
            setLoadingPage(false);

            setLoadingPage(true);
            await sendOptionAsMessage(optionKey, 'informar_ausencia');
            setLoadingPage(false);

            setOptions([{ key: 'finalizar', label: 'Sair' }]);

            let isaPergunta = 'Se precisar de algo mais, é só chamar!';
            setMessages((prev) => [
                ...prev,
                {
                    remetente: 'ISA',
                    texto: isaPergunta,
                    avatar: LogoImg,
                    isOption: false,
                },
            ]);

            await sendOptionAsMessage(isaPergunta, 'isa_pergunta');
            setShowOptions(true);
            setShowInputBar(false);
            return true;
        }

        /*

        if (optionKey === 'ausencia_contrato_mensal_nao' || optionKey === 'ausencia_confirmar_escala_trabalho_nao') {
            setNewTipoChat('ATENDIMENTO_AUSENCIA_PENDENTE');
            setLoadingPage(true);
            await sendOptionAsMessage(optionKey, 'informar_ausencia');
            setLoadingPage(false);
            await mudarStatusSessao('ATENDIMENTO_AUSENCIA_PENDENTE');
            setOptions([{ key: 'finalizar', label: 'Encerrar o Chat' }]);
            setShowOptions(true);
            setShowInputBar(true);
            return true;
        }

        if (optionKey === 'ausencia_contrato_mensal_cancelar') {
            setNewTipoChat('ausencia_contrato_mensal_cancelar');
            setLoadingPage(true);
            await sendOptionAsMessage(optionKey, 'informar_ausencia');
            setLoadingPage(false);
            await mudarStatusSessao('AUSENCIA_CANCELADA');
            setOptions([{ key: 'finalizar', label: 'Encerrar o Chat' }]);

            setMessages((prev) => [
                ...prev,
                {
                    remetente: 'ISA',
                    texto: 'Auto atendimento de ausência cancelado. Se precisar de algo mais, é só chamar!',
                    avatar: LogoImg,
                    isOption: false,
                },
            ]);
            setShowOptions(true);
            setShowInputBar(false);
            return true;
        }




        */

        if (optionKey === 'atribuir_ausencia') {
            setOptions([{ key: 'finalizar_atendimento', label: 'Encerrar o Chat' }]);
            await mudarStatusSessao('ATENDIMENTO_AUSENCIA_EM_ANDAMENTO');
            await atribuirSessaoChat('AUSENCIA_EM_ANDAMENTO');

            setOptions([{ key: 'finalizar_atendimento_ausencia', label: 'Encerrar o atendimento ausência' }]);
            setShowOptions(true);
            setShowInputBar(true);
            return true;
        }

        if (optionKey === 'finalizar_atendimento_ausencia') {
            setNewTipoChat(optionKey);
            setOptions([
                { key: 'finalizar_atendimento_ausencia_sim', label: 'Sim' },
                { key: 'finalizar_atendimento_ausencia_nao', label: 'Não' },
            ]);
            setShowOptions(true);
            setMessages((prev) => [
                ...prev,
                {
                    remetente: 'ISA',
                    texto: 'Você conseguiu resolver o problema?',
                    avatar: LogoImg,
                    isOption: false,
                },
            ]);
            setShowInputBar(false);
            return true;
        }

        if (optionKey === 'finalizar_atendimento_ausencia_nao') {
            await mudarStatusSessao('AUSENCIA_FINALIZADA');
            await atribuirSessaoChat('AUSENCIA_FINALIZADA');
            setMessages((prev) => [
                ...prev,
                {
                    remetente: 'ISA',
                    texto: 'Atendimento finalizado',
                    avatar: LogoImg,
                    isOption: false,
                },
            ]);

            setOptions([{ key: 'finalizar', label: 'Encerrar o Chat' }]);
            setShowInputBar(false);
            setShowOptions(true);
            return true;
        }

        if (optionKey === 'finalizar_atendimento_ausencia_sim') {
            await mudarStatusSessao('ATENDIMENTO_AUSENCIA_CONCLUIDO');
            await atribuirSessaoChat('AUSENCIA_CONCLUIDO');
            setMessages((prev) => [
                ...prev,
                {
                    remetente: 'ISA',
                    texto: 'Obrigado, volte sempre que precisar de ajuda!',
                    avatar: LogoImg,
                    isOption: false,
                },
            ]);

            setOptions([{ key: 'finalizar', label: 'Encerrar o Chat' }]);
            setShowInputBar(false);
            setShowOptions(true);
            return true;
        }
        return false;
    };

    const handleComunicacaoInternaAtendimentoModalInterativo = async (optionKey, optionLabel) => {
        if (optionKey === 'atribuir_comunicacao_interna') {
            setOptions([{ key: 'finalizar_atendimento_comunicacao_interna', label: 'Encerrar o Chat' }]);
            await mudarStatusSessao('COMUNICACAO_AT_HUMANDO');
            await atribuirSessaoChat('COMUNICACAO_EM_ANDAMENTO');

            setOptions([
                { key: 'ver_comunicacao_interna', label: 'Ver Comunicação Interna' },
                {
                    key: 'finalizar_atendimento_comunicacao_interna',
                    label: 'Encerrar o atendimento comunicação interna',
                },
            ]);
            setShowOptions(true);
            setShowInputBar(true);
            return true;
        }

        if (optionKey === 'finalizar_atendimento_comunicacao_interna') {
            setNewTipoChat(optionKey);
            setOptions([
                { key: 'finalizar_atendimento_comunicacao_interna_sim', label: 'Sim' },
                { key: 'finalizar_atendimento_comunicacao_interna_nao', label: 'Não' },
            ]);
            setShowOptions(true);
            setMessages((prev) => [
                ...prev,
                {
                    remetente: 'ISA',
                    texto: 'Você conseguiu resolver o problema?',
                    avatar: LogoImg,
                    isOption: false,
                },
            ]);
            setShowInputBar(false);
            return true;
        }

        if (optionKey === 'finalizar_atendimento_comunicacao_interna_sim') {
            await mudarStatusSessao('COMUNICACAO_FIN_HUMANDO');
            await atribuirSessaoChat('COMUNICACAO_RESOLVIDO');
            setMessages((prev) => [
                ...prev,
                {
                    remetente: 'ISA',
                    texto: 'Obrigado, volte sempre que precisar de ajuda!',
                    avatar: LogoImg,
                    isOption: false,
                },
            ]);

            setOptions([{ key: 'finalizar', label: 'Encerrar o Chat' }]);
            setShowInputBar(false);
            setShowOptions(true);
            return true;
        }

        if (optionKey === 'finalizar_atendimento_comunicacao_interna_nao') {
            await mudarStatusSessao('COMUNICACAO_N_RES_HUMANDO');
            await atribuirSessaoChat('COMUNICACAO_NAO_RESOLVIDO');
            setMessages((prev) => [
                ...prev,
                {
                    remetente: 'ISA',
                    texto: 'Obrigado, volte sempre que precisar de ajuda!',
                    avatar: LogoImg,
                    isOption: false,
                },
            ]);

            setOptions([{ key: 'finalizar', label: 'Encerrar o Chat' }]);
            setShowInputBar(false);
            setShowOptions(true);
            return true;
        }
        return false;
    };

    const handleComunicacaoInternaModal = async (optionKey, optionLabel) => {
        //comunicação interna modal
        if (optionKey === 'comunicacao_interna') {
            setNewTipoChat('comunicacao_interna');
            setMessages((prev) => [
                ...prev,
                {
                    remetente: 'ISA',
                    texto: 'Deseja realmente abrir uma Comunicação Interna?',
                    avatar: LogoImg,
                    isOption: false,
                },
            ]);
            setOptions([
                { key: 'comunicacao_interna_iniciar', label: 'Sim' },
                { key: 'voltar_questoes', label: 'Voltar para as questões anteriores' },
            ]);
            setShowOptions(true);
            setShowInputBar(false);
            return true;
        }

        if (optionKey === 'comunicacao_interna_iniciar') {
            //setNewTipoChat('comunicacao_interna_iniciar');
            await mudarStatusSessao('COMUNICACAO_PENDENTE');
            openAddMessageModal();
            setShowOptions(false);
            setShowInputBar(false);
            return true;
        }

        if (optionKey === 'comunicacao_interna_aguardando_atendimento') {
            //setNewTipoChat('comunicacao_interna_iniciar');
            setMessages((prev) => [
                ...prev,
                {
                    remetente: 'ISA',
                    texto: 'Em breve um atendente entrará em contato, por favor aguarde',
                    avatar: LogoImg,
                    isOption: false,
                },
            ]);

            setShowOptions(false);
            setShowInputBar(true);
            return true;
        }

        if (optionKey === 'comunicacao_interna_em_andamento') {
            setNewTipoChat('comunicacao_interna_em_andamento');
            await mudarStatusSessao('COMUNICACAO_PENDENTE');
            setMessages((prev) => [
                ...prev,
                {
                    remetente: 'ISA',
                    texto: 'Ok, por favor, click em Continuar com a Comunicação Interna ou em Cancelar Comunicação Interna',
                    avatar: LogoImg,
                    isOption: false,
                },
            ]);
            setOptions([
                { key: 'comunicacao_interna_cancelar', label: 'Cancelar Comunicação Interna' },
                { key: 'voltar_questoes', label: 'Voltar para as questões anteriores' },
            ]);
            setShowOptions(true);
            setShowInputBar(false);
            return true;
        }

        if (optionKey === 'comunicacao_interna_cancelar') {
            await mudarStatusSessao('COMUNICACAO_CANCELADO');
            setMessages((prev) => [
                ...prev,
                {
                    remetente: 'ISA',
                    texto: 'Comunicação Interna cancelada',
                    avatar: LogoImg,
                    isOption: false,
                },
            ]);
            setOptions([{ key: 'finalizar', label: 'Encerrar o Chat' }]);
            setShowOptions(true);
            setShowInputBar(false);
            return true;
        }

        if (optionKey === 'ver_comunicacao_interna') {
            if (comunicacaoInternaData?.cmit_id_comunicacao_interna) {
                openViewCommunicationModal(comunicacaoInternaData.cmit_id_comunicacao_interna);
                setShowOptions(true);
                return true;
            }

            setMessages((prev) => [
                ...prev,
                {
                    remetente: 'ISA',
                    texto: 'Parece que essa comunicação interna foi excluída, por favor entre em contato com o RH',
                    avatar: LogoImg,
                    isOption: false,
                },
            ]);
            setShowOptions(true);
            return true;
        }
        return false;
    };

    const handleSelectOptionComunicaoInternaInteracao = async (optionKey, optionLabel) => {
        //comunicação interna atendimento interativo

        if (optionKey === 'comunicacao_interna_interacao_pergunta') {
            setNewTipoChat('voltar_chat');
            setOptions([{ key: 'comunicacao_interna_interacao', label: 'Iniciar uma comunicação interna' }]);

            setShowOptions(true);
            setShowInputBar(true);
            return true;
        }

        if (optionKey === 'comunicacao_interna_interacao') {
            setNewTipoChat('comunicacao_interna');

            let isaPergunta = 'Gostaria de iniciar o processo de abertura de uma Comunicação Interna?';

            await sendOptionAsMessage(isaPergunta, 'isa_pergunta');

            setMessages((prev) => [
                ...prev,
                {
                    remetente: 'ISA',
                    texto: isaPergunta,
                    avatar: LogoImg,
                    isOption: false,
                },
            ]);

            setOptions([
                { key: 'comunicacao_interna_interacao_sim', label: 'Sim' },
                //{ key: 'voltar_questoes', label: 'Não' },
                { key: 'voltar_chat', label: 'Não' },
            ]);

            setShowOptions(true);
            return true;
        }

        if (optionKey === 'comunicacao_interna_interacao_sim') {
            setNewTipoChat('comunicacao_interna');
            setShowOptions(false);
            setLoadingPage(true);
            await mudarStatusSessao('COMUNICACAO_ANDAMENTO_INTERACAO');
            await sendOptionAsMessage('iniciar_comunicacao_interna', 'comunicacao_interna');
            setLoadingPage(false);

            let isaPergunta = 'Por favor, escolha a Regional:';

            await sendOptionAsMessage(isaPergunta, 'isa_pergunta');

            setMessages((prev) => [
                ...prev,
                {
                    remetente: 'ISA',
                    texto: isaPergunta,
                    avatar: LogoImg,
                    isOption: false,
                },
            ]);
            // Open regional selection modal
            if (openRegionalModal) {
                await openRegionalModal();
            }
            return true;
        }
        /**
         * What starts with 'comunicacao_interna_interacao_salvar_regional'
         */
        if (optionKey.startsWith('comunicacao_interna_interacao_salvar_regional')) {
            setNewTipoChat('comunicacao_interna');

            setShowOptions(false);
            setLoadingPage(true);
            await mudarStatusSessao('COMUNICACAO_ANDAMENTO_REGIONAL');
            await sendOptionAsMessage('regional_comunicacao_interna ' + optionLabel, 'comunicacao_interna');

            setLoadingPage(false);

            //let isaPergunta = 'Por favor, escolha uma das opções abaixo:';
            const optionsToSet = [
                { key: 'comunicacao_interna_interacao_area', label: 'Adicionar Area para Continuar o Processo' },
                { key: 'comunicacao_interna_interacao_sim', label: 'Mudar Regional' },
            ];

            setOptions([]);
            setOptions(optionsToSet);
            setShowOptions(true);
            setShowInputBar(false);
            return true;
        }

        if (optionKey === 'comunicacao_interna_interacao_area') {
            /*interação humana*/
            await sendOptionAsMessage(optionLabel, 'humano_expressar');

            setNewTipoChat('comunicacao_interna');
            setShowOptions(false);
            setLoadingPage(true);
            await mudarStatusSessao('COMUNICACAO_ANDAMENTO_AREA');
            setLoadingPage(false);

            let isaPergunta = 'Escolha a Área para a Abertura da Comunicação Interna:';
            setMessages((prev) => [
                ...prev,
                {
                    remetente: 'ISA',
                    texto: isaPergunta,
                    avatar: LogoImg,
                    isOption: false,
                },
            ]);

            await sendOptionAsMessage(isaPergunta, 'isa_pergunta');

            // Fetch areas dynamically from API using abstracted function
            setOptions([]);
            const areaOptions = await fetchAreasForCommunication();
            setOptions(areaOptions);
            setShowOptions(true);
            setShowInputBar(false);
            return true;
        }

        if (optionKey.startsWith('comunicacao_interna_interacao_area')) {
            setOptions([]);
            setNewTipoChat('comunicacao_interna');
            setShowOptions(false);
            setLoadingPage(true);
            await mudarStatusSessao('COMUNICACAO_ANDAMENTO_AREA');

            if (optionLabel !== 'Mudar o Tipo') {
                await sendOptionAsMessage('area_comunicacao_interna ' + optionLabel, 'comunicacao_interna');
            }

            if (optionLabel === 'Mudar o Tipo') {
                let comunicacaoData = await fetchCommunicationBySession(selectedSessionId);
                optionLabel = comunicacaoData?.cmit_area;
            }

            // Get area options to find the selected area ID
            const areaOptions = await fetchAreasForCommunication();
            const selectedArea = areaOptions.find((opt) => opt.label === optionLabel);

            setLastAreaChat(optionKey);

            if (!selectedArea || !selectedArea.id) {
                setLoadingPage(false);

                let isaPergunta = 'Erro ao identificar a área selecionada. Por favor, tente novamente.';
                setMessages((prev) => [
                    ...prev,
                    {
                        remetente: 'ISA',
                        texto: isaPergunta,
                        avatar: LogoImgSad,
                        isOption: false,
                    },
                ]);

                setOptions([{ key: 'comunicacao_interna_interacao_area', label: 'Selecionar área novamente' }]);
                setShowOptions(true);
                return true;
            }

            // Fetch tipos for the selected area
            const optionsTipo = await fetchTiposForCommunication(optionLabel);
            setLoadingPage(false);

            if (!optionsTipo || optionsTipo.length === 0) {
                let isaPergunta = 'Nenhum tipo disponível para esta área. Por favor, selecione outra área.';
                setMessages((prev) => [
                    ...prev,
                    {
                        remetente: 'ISA',
                        texto: isaPergunta,
                        avatar: LogoImgSad,
                        isOption: false,
                    },
                ]);

                setOptions([{ key: 'comunicacao_interna_interacao_area', label: 'Selecionar outra área' }]);
                setShowOptions(true);
                return true;
            }

            optionsTipo.push({ key: 'comunicacao_interna_interacao_area', label: 'Mudar o Área' });

            setOptions(optionsTipo);
            setShowOptions(true);
            setShowInputBar(false);
            return true;
        }

        const optionsTipos = await fetchTiposForCommunication();
        if (optionsTipos.some((option) => option.key === optionKey)) {
            setOptions([]);
            setNewTipoChat('comunicacao_interna');
            setShowOptions(false);
            setLoadingPage(true);
            await mudarStatusSessao('COMUNICACAO_ANDAMENTO_TIPO');

            await sendOptionAsMessage('tipo_comunicacao_interna ' + optionLabel, 'comunicacao_interna');

            setLoadingPage(false);

            //let isaPergunta = 'Opções abaixo:';
            //await sendOptionAsMessage(isaPergunta, 'isa_pergunta');

            const optionsToSet = [];

            //para áreas de atestados, adicionar opção específica
            if (
                lastAreaChat === 'comunicacao_interna_interacao_area_atestado_envio_de_documentos' ||
                lastAreaChat === 'comunicacao_interna_interacao_area_atestados'
            ) {
                optionsToSet.push({
                    key: 'comunicacao_interna_interacao_atestados_data_inicio_atestados',
                    label: 'Adicionar a Data de referência/Início para Continuar o Processo',
                });
            } else {
                optionsToSet.push({
                    key: 'comunicacao_interna_interacao_data_inicio',
                    label: 'Adicionar Data de Referência/Início para Continuar o Processo',
                });
            }

            if (lastTypeChat) {
                optionsToSet.push({ key: lastTypeChat, label: 'Mudar o Tipo' });
            }

            optionsToSet.push({ key: 'comunicacao_interna_interacao_area', label: 'Mudar o Área' });

            setOptions(optionsToSet);
            setShowOptions(true);
            setShowInputBar(false);
            return true;
        }

        if (optionKey === 'comunicacao_interna_interacao_data_inicio') {
            setNewTipoChat('data_inicio_comunicacao_interna');
            setShowOptions(false);
            setLoadingPage(true);
            await mudarStatusSessao('COMUNICACAO_ANDAMENTO_D_INICIAL_ALL');
            setLoadingPage(false);

            // Open date picker modal for date selection
            if (openDatePickerModal) {
                await openDatePickerModal('data_inicio_all', 'Selecione a Data de referência/Início');
            }
            return true;
        }

        if (optionKey === 'comunicacao_interna_interacao_data_inicio_enviar') {
            if (!selectedDate) {
                let isaPergunta = 'Por favor, selecione uma data primeiro.';
                setMessages((prev) => [
                    ...prev,
                    {
                        remetente: 'ISA',
                        texto: isaPergunta,
                        avatar: LogoImgSad,
                        isOption: false,
                    },
                ]);

                await sendOptionAsMessage(isaPergunta, 'isa_pergunta');
                return true;
            }

            // Format date as DD/MM/YYYY - extract year, month, day to avoid timezone issues
            const year = selectedDate.getFullYear();
            const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
            const day = String(selectedDate.getDate()).padStart(2, '0');
            const formattedDate = `${day}/${month}/${year}`;

            setNewTipoChat('data_inicio_comunicacao_interna');
            setShowOptions(false);
            setLoadingPage(true);
            await mudarStatusSessao('COMUNICACAO_ANDAMENTO_D_INICIAL_ALL');
            await sendOptionAsMessage('data_inicio_comunicacao_interna ' + formattedDate, 'comunicacao_interna');
            setLoadingPage(false);

            let isaPergunta = 'Data enviada com sucesso: ';
            setMessages((prev) => [
                ...prev,
                {
                    remetente: 'ISA',
                    texto: isaPergunta,
                    avatar: LogoImg,
                    isOption: false,
                },
            ]);

            setOptions([
                {
                    key: 'comunicacao_interna_interacao_adicionar_comentarios',
                    label: 'Adicionar um comentário para continuar com o processo',
                },
                {
                    key: 'comunicacao_interna_interacao_data_inicio',
                    label: 'Alterar a Data de referência/Início',
                },
                { key: 'comunicacao_interna_interacao_area', label: 'Mudar o Área' },
            ]);

            setShowOptions(true);
            setShowInputBar(false);
            return true;
        }

        if (optionKey === 'comunicacao_interna_interacao_atestados_data_inicio_atestados') {
            setNewTipoChat('data_inicio_comunicacao_interna');
            setShowOptions(false);
            setLoadingPage(true);
            await mudarStatusSessao('COMUNICACAO_ANDAMENTO_D_INICIAL');
            setLoadingPage(false);

            // Open date picker modal for date selection
            if (openDatePickerModal) {
                await openDatePickerModal('data_inicio', 'Selecione a Data de referência/Início:');
            }

            return true;
        }

        if (optionKey === 'comunicacao_interna_interacao_atestados_data_inicio_atestados_enviar') {
            if (!selectedDate) {
                let isaPergunta = 'Por favor, selecione uma data primeiro.';
                setMessages((prev) => [
                    ...prev,
                    {
                        remetente: 'ISA',
                        texto: isaPergunta,
                        avatar: LogoImgSad,
                        isOption: false,
                    },
                ]);
                return true;
            }

            // Format date as DD/MM/YYYY - extract year, month, day to avoid timezone issues
            const year = selectedDate.getFullYear();
            const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
            const day = String(selectedDate.getDate()).padStart(2, '0');
            const formattedDate = `${day}/${month}/${year}`;

            setNewTipoChat('data_inicio_comunicacao_interna');
            setShowOptions(false);
            setLoadingPage(true);
            await mudarStatusSessao('COMUNICACAO_ANDAMENTO_D_INICIAL');
            await sendOptionAsMessage('data_inicio_comunicacao_interna ' + formattedDate, 'comunicacao_interna');
            setLoadingPage(false);

            // let isaPergunta = 'Data enviada com sucesso. Agora, selecione uma das opções abaixo:';
            //setMessages((prev) => [
            //    ...prev,
            //    {
            //        remetente: 'ISA',
            //        texto: isaPergunta,
            //        avatar: LogoImg,
            //        isOption: false,
            //    },
            //]);

            //await sendOptionAsMessage(isaPergunta, 'isa_pergunta');

            setOptions([
                {
                    key: 'comunicacao_interna_interacao_atestados_data_fim',
                    label: 'Adicionar a Data Término para Continuar com o Processo',
                },
                {
                    key: 'comunicacao_interna_interacao_atestados_data_inicio_atestados',
                    label: 'Alterar a Data de referência/Início',
                },
                { key: 'comunicacao_interna_interacao_area', label: 'Mudar o Área' },
            ]);
            setShowOptions(true);
            setShowInputBar(false);
            return true;
        }

        if (optionKey === 'comunicacao_interna_interacao_atestados_data_fim') {
            setNewTipoChat('data_fim_comunicacao_interna');
            setShowOptions(false);
            setLoadingPage(true);
            await mudarStatusSessao('COMUNICACAO_ANDAMENTO_D_FINAL');
            setLoadingPage(false);

            // Open date picker modal for end date selection
            if (openDatePickerModal) {
                await openDatePickerModal('data_fim', 'Selecione a Data Término:');
            }

            return true;
        }

        if (optionKey === 'comunicacao_interna_interacao_atestados_data_fim_enviar') {
            if (!selectedDate) {
                let isaPergunta = 'Por favor, selecione a data final.';
                setMessages((prev) => [
                    ...prev,
                    {
                        remetente: 'ISA',
                        texto: isaPergunta,
                        avatar: LogoImgSad,
                        isOption: false,
                    },
                ]);

                await sendOptionAsMessage(isaPergunta, 'isa_pergunta');
                return true;
            }

            // Format date as DD/MM/YYYY - extract year, month, day to avoid timezone issues
            const year = selectedDate.getFullYear();
            const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
            const day = String(selectedDate.getDate()).padStart(2, '0');
            const formattedDate = `${day}/${month}/${year}`;

            setNewTipoChat('data_fim_comunicacao_interna');
            setShowOptions(false);
            setLoadingPage(true);
            await mudarStatusSessao('COMUNICACAO_ANDAMENTO_D_FINAL');
            await sendOptionAsMessage('data_fim_comunicacao_interna ' + formattedDate, 'comunicacao_interna');
            setLoadingPage(false);

            let isaPergunta =
                'Data de fim enviada com sucesso.. Agora, selecione uma das opções abaixo, Lembrando que o CID ou os comentários precisarão ser preenchidos para continuar o processo:';

            setMessages((prev) => [
                ...prev,
                {
                    remetente: 'ISA',
                    texto: isaPergunta,
                    avatar: LogoImg,
                    isOption: false,
                },
            ]);

            await sendOptionAsMessage(isaPergunta, 'isa_pergunta');

            setOptions([
                {
                    key: 'comunicacao_interna_interacao_adicionar_cid',
                    label: 'Enviar CID (Se aplicável)',
                },
                { key: 'comunicacao_interna_interacao_adicionar_comentarios', label: 'Adicionar Comentários' },
                {
                    key: 'comunicacao_interna_interacao_atestados_data_inicio_atestados',
                    label: 'Alterar a Data de referência/Início',
                },
                {
                    key: 'comunicacao_interna_interacao_atestados_data_fim',
                    label: 'Alterar a Data Término',
                },
                { key: 'comunicacao_interna_interacao_area', label: 'Mudar o Área' },
            ]);
            setShowOptions(true);
            setShowInputBar(false);
            return true;
        }

        if (optionKey === 'comunicacao_interna_interacao_adicionar_cid') {
            setNewTipoChat('cid_comunicacao_interna');
            setShowOptions(false);
            setLoadingPage(true);
            await mudarStatusSessao('COMUNICACAO_ANDAMENTO_CID');
            setLoadingPage(false);

            let isaPergunta =
                'Por favor digite o CID abaixo, após o preenchimento, clique em "Adicionar Comentários para continuar o processo" abaixo:';
            setMessages((prev) => [
                ...prev,
                {
                    remetente: 'ISA',
                    texto: isaPergunta,
                    avatar: LogoImg,
                    isOption: false,
                },
            ]);

            await sendOptionAsMessage(isaPergunta, 'isa_pergunta');

            setOptions([
                {
                    key: 'comunicacao_interna_interacao_adicionar_comentarios',
                    label: 'Adicionar Comentários para continuar o processo',
                },
                { key: 'comunicacao_interna_interacao_area', label: 'Mudar o Área' },
            ]);
            setShowOptions(true);
            setShowInputBar(true);
            return true;
        }

        if (optionKey === 'comunicacao_interna_interacao_adicionar_comentarios') {
            setNewTipoChat('comentario_comunicacao_interna');
            setShowOptions(false);
            setLoadingPage(true);
            await mudarStatusSessao('COMUNICACAO_ANDAMENTO_COMENTARIO');
            setLoadingPage(false);

            let isaPergunta =
                'Por favor adicione algum comentário, isso é muito importante para entendermos melhor a sua situação. Observação: será registrado no Formulário de Comunicação Interna apenas o último comentário enviado aqui no chat.';

            setMessages((prev) => [
                ...prev,
                {
                    remetente: 'ISA',
                    texto: isaPergunta,
                    avatar: LogoImg,
                    isOption: false,
                },
            ]);

            await sendOptionAsMessage(isaPergunta, 'isa_pergunta');

            const communicationData = await fetchCommunicationBySession(selectedSessionId);
            let areaData = await fetchAreasForCommunication(communicationData.cmit_area);
            let isFileUpload = areaData[0]['attachment_is_required'];

            const optionSet = [];

            if (!isFileUpload) {
                optionSet.push({
                    key: 'comunicacao_interna_interacao_anexo',
                    label: 'Enviar anexo ',
                });
                optionSet.push({
                    key: 'comunicacao_interna_interacao_finalizar',
                    label: 'Finalizar Comunicação Interna',
                });
            }

            if (isFileUpload) {
                optionSet.push({
                    key: 'comunicacao_interna_interacao_anexo',
                    label: 'Por favor, anexe o arquivo para darmos continuidade às etapas finais',
                });
            }

            optionSet.push({ key: 'comunicacao_interna_interacao_area', label: 'Mudar o Área' });

            setOptions(optionSet);
            setShowOptions(true);
            setShowInputBar(true);
            return true;
        }

        if (optionKey === 'comunicacao_interna_interacao_anexo') {
            setNewTipoChat('comunicacao_interna');
            setShowOptions(false);
            setLoadingPage(true);
            await mudarStatusSessao('COMUNICACAO_ANDAMENTO_ARQUIVO');
            setLoadingPage(false);

            const optionSet = [];
            const communicationData = await fetchCommunicationBySession(selectedSessionId);
            // Set communication data in state for file upload
            if (communicationData && communicationData.cmit_id_comunicacao_interna) {
                setComunicacaoInternaData(communicationData);
            }

            let areaData = await fetchAreasForCommunication(communicationData.cmit_area);
            let isFileUpload = areaData[0]['attachment_is_required'];

            if (!isFileUpload) {
                optionSet.push({
                    key: 'comunicacao_interna_interacao_anexo',
                    label: 'Enviar anexo',
                });
                optionSet.push({
                    key: 'comunicacao_interna_interacao_finalizar',
                    label: 'Finalizar Comunicação Interna',
                });
            }

            if (isFileUpload) {
                optionSet.push({
                    key: 'comunicacao_interna_interacao_anexo',
                    label: 'Por favor, anexe o arquivo para darmos continuidade às etapas finais',
                });
            }

            optionSet.push({ key: 'comunicacao_interna_interacao_area', label: 'Mudar o Área' });

            setOptions(optionSet);
            setShowOptions(true);

            let isaPergunta = 'Selecione o arquivo para anexar:';

            setMessages((prev) => [
                ...prev,
                {
                    remetente: 'ISA',
                    texto: isaPergunta,
                    avatar: LogoImg,
                    isOption: false,
                },
            ]);

            // Trigger file upload
            try {
                const result = await handleComunicacaoInternaFileUpload();
                if (result && result.success) {
                    let isaPergunta = 'Arquivo anexado com sucesso!';
                    setMessages((prev) => [
                        ...prev,
                        {
                            remetente: 'ISA',
                            texto: isaPergunta,
                            avatar: LogoImg,
                            isOption: false,
                        },
                    ]);

                    await sendOptionAsMessage(isaPergunta, 'isa_pergunta');
                }
            } catch (error) {
                // Only show error message if it's not a cancellation
                const errorMessage = typeof error === 'string' ? error : error.message;
                if (errorMessage && errorMessage !== 'No file selected or missing required data') {
                    console.error('Error uploading file:', error);

                    let isaPergunta = 'Erro ao fazer upload do arquivo. Tente novamente.';

                    setMessages((prev) => [
                        ...prev,
                        {
                            remetente: 'ISA',
                            texto: isaPergunta,
                            avatar: LogoImg,
                            isOption: false,
                        },
                    ]);
                }
                // If user cancelled, just continue without error message
            }
            setOptions([]);
            setLoadingPage(true);

            let isaPergunta2 = 'Agora, escolha uma das opções abaixo:';

            setMessages((prev) => [
                ...prev,
                {
                    remetente: 'ISA',
                    texto: isaPergunta2,
                    avatar: LogoImg,
                    isOption: false,
                },
            ]);
            //await sendOptionAsMessage(isaPergunta2, 'isa_pergunta');

            setLoadingPage(false);

            setOptions([
                { key: 'comunicacao_interna_interacao_anexo', label: 'Enviar anexo novamente' },
                { key: 'comunicacao_interna_interacao_finalizar', label: 'Finalizar Comunicação Interna' },
                { key: 'comunicacao_interna_interacao_area', label: 'Mudar o Área' },
            ]);
            setShowOptions(true);
            setShowInputBar(false);
            return true;
        }

        if (optionKey === 'comunicacao_interna_interacao_finalizar') {
            setNewTipoChat('conversar_comunicacao_interna');
            setShowOptions(false);
            setLoadingPage(true);
            await mudarStatusSessao('COMUNICACAO_RESPONSAVEL_PENDENTE');
            await sendOptionAsMessage('fim_comunicacao_interna', 'comunicacao_interna');

            setLoadingPage(false);

            let isaPergunta3 = 'Comunicação Interna finalizada com sucesso! Se precisar de algo mais, é só chamar!';

            setMessages((prev) => [
                ...prev,
                {
                    remetente: 'ISA',
                    texto: isaPergunta3,
                    avatar: LogoImg,
                    isOption: false,
                },
            ]);

            setNewTipoChat('finalizar');

            setOptions([{ key: 'finalizar', label: 'Encerrar o Chat' }]);
            setShowOptions(true);
            setShowInputBar(true);
            return true;
        }

        return false;
    };
    return handleSelectOption;
};

export default useHandleSelectOption;
